import { injectable } from 'inversify';
import { Collection, Db, getMongoDb } from "@swoft/persistence";
import { Project } from '../../domain/aggregates/Project';
import { GTD_COLLECTIONS } from '../../../../config/collections';

/**
 * MongoDB Repository for Project Aggregate
 * 
 * Following Eric Evans repository pattern - provides collection-like
 * interface for accessing projects while hiding persistence details.
 */
@injectable()
export class ProjectRepository {
  private db: Db | null = null;
  private collection: Collection | null = null;

  constructor() {
    // Lazy initialization - don't connect until needed
  }

  private ensureConnection() {
    if (!this.db) {
      this.db = getMongoDb();
      this.collection = this.db.collection(GTD_COLLECTIONS.PROJECTS);
    }
    return this.collection!;
  }

  async save(project: Project): Promise<void> {
    const collection = this.ensureConnection();
    const doc = {
      _id: project.id,
      name: project.name,
      desiredOutcome: project.desiredOutcome,
      createdBy: project.createdBy,
      createdAt: project.createdAt,
      area: project.area,
      reviewDate: project.reviewDate,
      status: project.status,
      nextActionIds: project.nextActionIds,
      completedAt: project.completedAt,
      updatedAt: new Date()
    };

    await collection.replaceOne(
      { _id: project.id as any },
      doc,
      { upsert: true }
    );
  }

  async findById(id: string): Promise<Project | null> {
    const collection = this.ensureConnection();
    const doc = await collection.findOne({ _id: id as any });
    if (!doc) return null;

    return this.mapDocumentToDomain(doc);
  }

  async findByStatus(status: string): Promise<Project[]> {
    const collection = this.ensureConnection();
    const docs = await collection
      .find({ status })
      .sort({ createdAt: -1 })
      .toArray();

    return docs
      .map(doc => this.safeMapDocumentToDomain(doc))
      .filter(project => project !== null) as Project[];
  }

  async findByArea(area: string): Promise<Project[]> {
    const collection = this.ensureConnection();
    const docs = await collection
      .find({ area })
      .sort({ createdAt: -1 })
      .toArray();

    return docs
      .map(doc => this.safeMapDocumentToDomain(doc))
      .filter(project => project !== null) as Project[];
  }

  async findAll(): Promise<Project[]> {
    const collection = this.ensureConnection();
    const docs = await collection
      .find({})
      .sort({ createdAt: -1 })
      .toArray();

    return docs
      .map(doc => this.safeMapDocumentToDomain(doc))
      .filter(project => project !== null) as Project[];
  }

  async deleteById(id: string): Promise<void> {
    const collection = this.ensureConnection();
    await collection.deleteOne({ _id: id as any });
  }

  private mapDocumentToDomain(doc: any): Project {
    const project = new Project(
      doc._id,
      doc.name,
      doc.desiredOutcome,
      doc.createdBy,
      new Date(doc.createdAt),
      doc.area,
      doc.reviewDate ? new Date(doc.reviewDate) : undefined
    );

    // Restore private state
    if (doc.nextActionIds) {
      (project as any)._nextActionIds = doc.nextActionIds;
    }
    if (doc.status) {
      (project as any)._status = doc.status;
    }
    if (doc.completedAt) {
      (project as any)._completedAt = new Date(doc.completedAt);
    }

    return project;
  }

  private safeMapDocumentToDomain(doc: any): Project | null {
    try {
      // Validate required fields before attempting to create domain object
      if (!doc.name?.trim()) {
        console.warn(`Skipping project document with missing name: ${doc._id}`);
        return null;
      }
      if (!doc.desiredOutcome?.trim()) {
        console.warn(`Skipping project document with missing desiredOutcome: ${doc._id}`);
        return null;
      }

      return this.mapDocumentToDomain(doc);
    } catch (error) {
      console.warn(`Failed to map project document to domain object: ${doc._id}`, error);
      return null;
    }
  }
}